// MarketPulseTicker.tsx
// Auto-scrolling compact ticker strip for the Adversyn Trading UI (Vite/React).
// Pulls from three real backend sources and merges:
//   1. /api/v1/market-pulse  (top_tradable_movers: equity, ETF, forex, news,
//      volatility — all priced) PLUS the existing ticker payload as fallback
//   2. /api/v1/market-pulse/equities/small-account-candidates  (top SAC picks)
// Items without a real price are filtered out (no "—" placeholder rows).
// Items are de-duplicated by symbol so the ticker never shows the same
// symbol twice. Group order: forex → indexes/ETFs → equities → crypto → SAC.
// Pauses on hover. Graceful degraded state. Never shows raw backend error text.

import { useEffect, useRef, useState } from "react";

import { apiV1Base } from '../lib/apiConfig';
interface TickerItem {
  symbol: string;
  price?: number | null;
  change_pct?: number | null;
  change_percent?: number | null;
  status?: string;
  freshness_status?: string;
  tradable?: boolean;
  is_tradable?: boolean;
  tradeable?: boolean;
  trading_enabled?: boolean;
  asset_class?: string;
  assetClass?: string;
}

function isTradable(item: TickerItem): boolean {
  return (
    item.tradable !== false &&
    item.is_tradable !== false &&
    item.tradeable !== false &&
    item.trading_enabled !== false
  );
}

function isRiskOnly(symbol: string): boolean {
  return ["VIX", "^VIX", "$VIX"].includes(symbol.trim().toUpperCase());
}

function getChangePct(item: TickerItem): number | null {
  const v = item.change_pct ?? item.change_percent ?? null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function formatChange(pct: number | null): string {
  if (pct == null) return "";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function formatPrice(price: number | null | undefined, assetClass?: string): string {
  if (price == null || !Number.isFinite(price)) return "—";
  if (assetClass === "forex") return price.toFixed(4);
  return price >= 1000
    ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : price.toFixed(price < 1 ? 4 : 2);
}

function apiBase(): string {
  const host = typeof window !== "undefined" ? window.location.hostname : "localhost";
  return apiV1Base();
}


function tickerDuration(count: number): number {
  // Aim for ~0.9s of dwell per item so a longer ticker is not unreadably
  // fast. The animation translates -50% of the (doubled) track, so the
  // duration is roughly count * 0.6, clamped to a reasonable range.
  if (count <= 5)  return 4;
  if (count <= 10) return 5;
  if (count <= 15) return 7;
  if (count <= 20) return 9;
  if (count <= 30) return 12;
  if (count <= 40) return 15;
  return 17;
}

export function MarketPulseTicker() {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [status, setStatus] = useState<"loading" | "live" | "stale" | "unavailable">("loading");
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Group keys we accept from /market-pulse top_tradable_movers, in display
  // order. Group label is what appears as a small caption between groups.
  const MOVER_GROUPS: Array<[string, string, string]> = [
    ["Top Tradable Forex Movers",     "forex",  "FX"],
    ["Top Tradable Equity Movers",    "equity", "EQUITIES"],
    ["Top Tradable ETF Movers",       "etf",    "ETFs"],
    ["News-Driven Tradable Movers",   "news",   "NEWS"],
    ["Volatility-Spike Tradable Movers", "vol", "VOL"],
  ];

  // Stable order for ticker items by asset class so the visual grouping is
  // predictable regardless of how the backend sequences the lists.
  const ASSET_CLASS_ORDER: Record<string, number> = {
    forex: 0, fx: 0,
    index: 1, indexes: 1,
    etf: 2,
    equity: 3, equities: 3, stock: 3, stocks: 3,
    crypto: 4,
    "small-account": 5, small_account: 5, sac: 5,
    news: 6, vol: 7,
  };

  function hasRealPrice(it: TickerItem | null | undefined): boolean {
    return !!it
      && typeof it.price === "number"
      && Number.isFinite(it.price as number)
      && (it.price as number) > 0;
  }

  function groupRank(cls: string | undefined): number {
    const k = (cls || "").toLowerCase();
    return ASSET_CLASS_ORDER[k] ?? 99;
  }

  async function fetchJson(path: string, timeoutMs = 5000): Promise<any | null> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${apiBase()}${path}`, { cache: "no-store", signal: ctrl.signal });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    } finally {
      clearTimeout(t);
    }
  }

  async function fetchTicker() {
    // Pull the rolled-up market-pulse payload (gives us ticker + movers)
    // and the small-account candidates list. Both are best-effort; a
    // partial failure still produces a useful ticker.
    const [mpJson, sacJson] = await Promise.all([
      fetchJson("/market-pulse/ticker"), // ADV-MKT-PREDEPLOY-003C: 72KB cached ticker payload, not the 1.35MB full /market-pulse
      fetchJson("/market-pulse/equities/small-account-candidates?limit=6"),
    ]);

    const merged: TickerItem[] = [];
    const seen = new Set<string>();
    function pushUnique(it: TickerItem | undefined | null, defaultClass?: string): void {
      if (!it || !it.symbol) return;
      const key = String(it.symbol).trim().toUpperCase();
      if (!key || seen.has(key)) return;
      if (isRiskOnly(key)) return;
      if (!isTradable(it)) return;
      // hasRealPrice filter removed 2026-05-15 — let symbols without quotes (e.g. SPY/QQQ benchmarks when off-market) render with '—' price
      // so the ticker shows the FULL tradable universe, not just symbols Alpaca has cached this cycle.
      seen.add(key);
      merged.push({ ...it, asset_class: it.asset_class ?? it.assetClass ?? defaultClass });
    }

    let freshnessTag = "";
    const mpData = (mpJson?.ok !== undefined ? (mpJson.data ?? mpJson) : mpJson) || {};
    if (mpData) {
      // 1. top_tradable_movers — these come pre-priced and pre-classed
      const movers = mpData.top_tradable_movers || mpData.topTradableMovers || {};
      for (const [groupKey, _cls, _label] of MOVER_GROUPS) {
        const list: TickerItem[] = Array.isArray(movers[groupKey]) ? movers[groupKey] : [];
        for (const it of list) pushUnique(it, _cls);
      }
      // 2. ticker payload (forex pairs that are not in the movers list)
      const tickerArr: TickerItem[] = Array.isArray(mpData.ticker) ? mpData.ticker : [];
      for (const it of tickerArr) pushUnique(it);
      const f = String(
        mpData.freshness_status ?? mpData.status ?? mpData.overall_status ?? ""
      ).toUpperCase();
      freshnessTag = f;
    }

    // 3. Small-account candidates — top movers in the $2-$20 universe.
    const sacData = (sacJson?.ok !== undefined ? (sacJson.data ?? sacJson) : sacJson) || {};
    const sacList: TickerItem[] = Array.isArray(sacData?.candidates) ? sacData.candidates : [];
    for (const it of sacList) pushUnique(it, "small-account");

    if (merged.length === 0) {
      setStatus("unavailable");
      setItems([]);
      return;
    }

    // Stable group order: forex → indexes → ETFs → equities → crypto → SAC.
    merged.sort((a, b) => groupRank(a.asset_class) - groupRank(b.asset_class));

    // Hard ceiling so the marquee never gets so long that it crawls.
    const MAX_TICKER_ITEMS = 80;  // 2026-05-15: bumped from 40 so the shell ticker shows the full tradable universe (forex + ETFs + benchmark ETFs + movers + SAC), not just the top 40
    setItems(merged.slice(0, MAX_TICKER_ITEMS));
    setStatus(
      freshnessTag.includes("LIVE") ? "live"
      : freshnessTag.includes("STALE") || freshnessTag.includes("CACHE") ? "stale"
      : "live"
    );
  }

  useEffect(() => {
    fetchTicker();
    intervalRef.current = setInterval(fetchTicker, 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  if (status === "unavailable" && items.length === 0) {
    return (
      <div style={{
        background: "#161b22",
        borderBottom: "1px solid #21262d",
        padding: "6px 16px",
        fontSize: 12,
        color: "#6e7681",
      }}>
        Market data unavailable
      </div>
    );
  }

  const dotColor = status === "live" ? "#3fb950" : status === "stale" ? "#d29922" : "#6e7681";

  return (
    <div
      style={{
        background: "#161b22",
        borderBottom: "1px solid #21262d",
        overflow: "hidden",
        height: 36,
        display: "flex",
        alignItems: "center",
        userSelect: "none",
        position: "relative",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Status label — pinned left */}
      <div style={{
        flexShrink: 0,
        padding: "0 12px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        borderRight: "1px solid #21262d",
        fontSize: 10,
        color: "#6e7681",
        textTransform: "uppercase",
        letterSpacing: 1,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: "50%",
          background: dotColor,
          display: "inline-block",
        }} />
        PULSE
      </div>

      {/* Scrolling track */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {items.length === 0 ? (
          <div style={{ padding: "0 16px", fontSize: 12, color: "#6e7681" }}>
            Loading ticker…
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: 0,
              whiteSpace: "nowrap",
              animation: paused ? "none" : `mp-vite-ticker-scroll ${tickerDuration(items.length)}s linear infinite`,
              willChange: "transform",
            }}
          >
            {[...items, ...items].map((item, i) => {
              const changePct = getChangePct(item);
              const up = (changePct ?? 0) > 0;
              const down = (changePct ?? 0) < 0;
              const cls = item.asset_class ?? item.assetClass;
              return (
                <span
                  key={`${item.symbol}-${i}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "0 20px 0 0",
                    fontSize: 12,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#e6edf3" }}>{item.symbol}</span>
                  {item.price != null && (
                    <>
                      <span style={{ color: "#a0a3ad" }}>{formatPrice(item.price, cls)}</span>
                      {changePct != null && (
                        <span style={{
                          color: up ? "#3fb950" : down ? "#f85149" : "#6e7681",
                          fontWeight: 500,
                        }}>
                          {formatChange(changePct)}
                        </span>
                      )}
                    </>
                  )}
                  {item.price == null && <span style={{ color: "#6e7681" }}>—</span>}
                  <span style={{ color: "#21262d", marginLeft: 8 }}>·</span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes mp-vite-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default MarketPulseTicker;
